import React, { FC, ReactNode, useCallback, useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared";
import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import { AnyEntity, Deal, EntityTypeSupportingPreview } from "@mapmycustomers/shared/types/entity";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import DateRange from "@mapmycustomers/shared/types/range/DateRange";
import ViewState from "@mapmycustomers/shared/types/viewModel/ViewState";

import { getDatesRange } from "@app/enum/dashboard/dateRanges";
import {
  DrillDownModal,
  DrillDownModalProps,
} from "@app/scene/dashboard/components/cards/common/DrillDownModal";
import {
  exportClosedStageDealCountCardDrillDownData,
  fetchClosedStageDealCountCardDrillDownData,
} from "@app/scene/dashboard/store/cards/closedStageDealCount/actions";
import {
  getClosedStageDealCountDrillDownCustomDateRange,
  getClosedStageDealCountDrillDownTotalFilteredRecords,
  getClosedStageDealCountDrillDownViewState,
  isClosedStageDealCountDrillDownLoading,
} from "@app/scene/dashboard/store/cards/closedStageDealCount/selectors";
import { DrillDownListFetcherPayload } from "@app/scene/dashboard/types/DrillDownListFetcher";
import formatDrillDownDateRange from "@app/scene/dashboard/utils/formatDrillDownDateRange";
import { getFunnelStages } from "@app/store/deal";
import { showEntityView } from "@app/store/entityView/actions";
import { RootState } from "@app/store/rootReducer";
import ClosedStageDealCountCardConfiguration from "@app/types/dashboard/cards/closedStageDealCount/ClosedStageDealCountCardConfiguration";
import Scope from "@app/types/dashboard/Scope";
import { getDealStageTypeDisplayName } from "@app/util/ui";

import messages from "../../messages";

interface Props extends Pick<DrillDownModalProps, "onEdit" | "onHide"> {
  configuration: ClosedStageDealCountCardConfiguration;
  customDateRange?: DateRange;
  onExportDrillDownData: typeof exportClosedStageDealCountCardDrillDownData;
  onFetchDrillDownData: (payload: DrillDownListFetcherPayload<Deal>) => void;
  onShowEntityView: typeof showEntityView;
  scope: Scope;
  stages: Stage[];
  totalFilteredRecords: number;
  viewState: ViewState;
}

const DrillDown: FC<Props> = ({
  configuration,
  customDateRange,
  onEdit,
  onExportDrillDownData,
  onFetchDrillDownData,
  onHide,
  onShowEntityView,
  scope,
  stages,
  totalFilteredRecords,
  viewState,
}) => {
  const intl = useIntl();

  const handleExport = useCallback(() => {
    onExportDrillDownData({ configuration, scope });
  }, [onExportDrillDownData, configuration, scope]);

  const handleRecordView = useCallback(
    (entity: AnyEntity) => {
      onShowEntityView({
        entityId: entity.id,
        entityType: entity.entity as EntityTypeSupportingPreview,
        zIndex: 1001, // to override the drill down modal
      });
    },
    [onShowEntityView]
  );

  const stageTypes = useMemo(() => {
    const stageTypes = [];
    const dealClosedStages = stages.filter(({ id }) =>
      configuration.criteria.dealStageIds.includes(id)
    );

    const hasClosedWon = dealClosedStages.some(({ type }) => type === DealStageType.WON);
    if (hasClosedWon) {
      stageTypes.push(DealStageType.WON);
    }

    const hasClosedLost = dealClosedStages.some(({ type }) => type === DealStageType.LOST);
    if (hasClosedLost) {
      stageTypes.push(DealStageType.LOST);
    }
    return stageTypes;
  }, [configuration.criteria.dealStageIds, stages]);

  return (
    <DrillDownModal
      entityType={EntityType.DEAL}
      onEdit={onEdit}
      onExportDrillDownData={handleExport}
      onFetchDrillDownData={onFetchDrillDownData}
      onHide={onHide}
      onRecordView={handleRecordView}
      subtitle={formatDrillDownDateRange(
        customDateRange ??
          getDatesRange(configuration.dateRange.range, configuration.dateRange.subRange)
      )}
      title={
        configuration.customTitle?.trim() ||
        intl.formatMessage(messages.cardTitle, {
          date: (text: ReactNode[]) => text,
          dateRange: configuration.dateRange.range,
          dateSubRange: configuration.dateRange.subRange,
          entityList: intl.formatList(
            stageTypes.map((type) => getDealStageTypeDisplayName(intl, type)),
            { type: "conjunction" }
          ),
          stageTypes: stageTypes.length > 0,
        })
      }
      totalFilteredRecords={totalFilteredRecords}
      viewState={viewState}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  customDateRange: getClosedStageDealCountDrillDownCustomDateRange(state),
  loading: isClosedStageDealCountDrillDownLoading(state),
  stages: Object.values(getFunnelStages(state)).flat(),
  totalFilteredRecords: getClosedStageDealCountDrillDownTotalFilteredRecords(state),
  viewState: getClosedStageDealCountDrillDownViewState(state),
});

const mapDispatchToProps = {
  onExportDrillDownData: exportClosedStageDealCountCardDrillDownData,
  onFetchDrillDownData: fetchClosedStageDealCountCardDrillDownData.request,
  onShowEntityView: showEntityView,
};

export default connect(mapStateToProps, mapDispatchToProps)(DrillDown);
